/**
 * 턴어라운드 뷰 세트 — 캐릭터를 여러 방위에서 찍은 장면들.
 *
 * Meshy 가 캐릭터를 입체로 만드는 방식이 이것이다: 정면·측면·후면 세 장이 아니라
 * **45° 간격 여덟 장**. 뷰가 늘수록 비주얼 헐이 깎아 내는 부위가 늘고(대각선 방향의
 * 살), 텍스처가 법선을 정면으로 받는 뷰가 늘어 늘어짐이 줄어든다.
 *
 * 세 뷰 시트는 이 형식의 특수 경우다(방위 0°, 90°, 180°). 파일 여러 장을 떨어뜨리면
 * 장수에 따라 방위를 배정한다: 8장=45° 간격, 4장=90° 간격, 3장=정면·측면·후면.
 *
 * 방위 규약: 0° 정면(+z 에서 봄), 90° 오른쪽 측면(+x 에서 봄, 캐릭터가 이미지 왼쪽을
 * 봄), 180° 후면, 270° 왼쪽 측면. 카메라 방향 d = (sinθ, 0, cosθ), 화면 오른쪽
 * r = (cosθ, 0, −sinθ).
 */

import type { Field, Raster } from '../types.ts';
import { extractSilhouette } from '../imaging/silhouette.ts';
import type { SheetSplit } from '../sheet/split.ts';

export interface ViewImage {
  /** 도(°). 0 = 정면. */
  azimuth: number;
  raster: Raster;
  mask: Field;
  bbox: { x0: number; y0: number; x1: number; y1: number };
  /** 실루엣 폭/높이 비 */
  aspect: number;
}

export interface TurnaroundSet {
  views: ViewImage[];
  /** 뷰가 셋인 시트에서 왔는지(측면 하나만 있음) */
  source: 'sheet' | 'files' | 'api';
}

function bboxOf(mask: Field) {
  const { width: w, height: h } = mask;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask.data[y * w + x]! < 0.5) continue;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return { x0: 0, y0: 0, x1: w - 1, y1: h - 1 };
  return { x0, y0, x1, y1 };
}

export function makeView(azimuth: number, raster: Raster, mask?: Field): ViewImage {
  const m = mask ?? extractSilhouette(raster).mask;
  const bbox = bboxOf(m);
  const aspect = Math.max(1, bbox.x1 - bbox.x0) / Math.max(1, bbox.y1 - bbox.y0);
  return { azimuth, raster, mask: m, bbox, aspect };
}

/** 장수에 맞는 방위 배정. */
export function azimuthsFor(count: number): number[] {
  if (count === 3) return [0, 90, 180];
  if (count === 2) return [0, 90];
  if (count === 1) return [0];
  return Array.from({ length: count }, (_, i) => (i * 360) / count);
}

/**
 * @param azimuths 방위를 직접 준다(잡에서 온 뷰처럼 일부가 빠질 수 있을 때). 없으면 장수로 배정.
 */
export function fromRasters(
  rasters: Raster[],
  source: TurnaroundSet['source'] = 'files',
  azimuths?: number[],
): TurnaroundSet {
  const az = azimuths && azimuths.length === rasters.length ? azimuths : azimuthsFor(rasters.length);
  return mirrorFill({ views: rasters.map((r, i) => makeView(az[i]!, r)), source });
}

/** 파일 이름 `view-045.webp` 식에서 방위를 읽는다. 전부 읽히면 그걸 쓰고, 아니면 undefined. */
export function azimuthsFromNames(names: string[]): number[] | undefined {
  const out: number[] = [];
  for (const n of names) {
    const m = /(?:^|[^0-9])(\d{3})(?:[^0-9]|$)/.exec(n.replace(/\.[a-z0-9]+$/i, ''));
    if (!m) return undefined;
    const a = Number(m[1]);
    if (a < 0 || a >= 360) return undefined;
    out.push(a);
  }
  return new Set(out).size === out.length ? out : undefined;
}

/** 3뷰 시트 → 뷰 세트. 패널 마스크는 시트 분할에서 이미 뽑아 두었다. */
export function fromSheet(split: SheetSplit): TurnaroundSet {
  const az = [0, 90, 180];
  return mirrorFill({
    views: split.panels.map((p, i) => makeView(az[i]!, p.raster, p.mask)),
    source: 'sheet',
  });
}

function mirrorRaster(r: Raster): Raster {
  const out: Raster = { width: r.width, height: r.height, data: new Uint8ClampedArray(r.data.length) };
  for (let y = 0; y < r.height; y++) {
    for (let x = 0; x < r.width; x++) {
      const si = (y * r.width + x) * 4;
      const di = (y * r.width + (r.width - 1 - x)) * 4;
      out.data[di] = r.data[si]!; out.data[di + 1] = r.data[si + 1]!; out.data[di + 2] = r.data[si + 2]!; out.data[di + 3] = r.data[si + 3]!;
    }
  }
  return out;
}

function mirrorField(f: Field): Field {
  const out: Field = { width: f.width, height: f.height, data: new Float32Array(f.data.length) };
  for (let y = 0; y < f.height; y++) {
    for (let x = 0; x < f.width; x++) out.data[y * f.width + (f.width - 1 - x)] = f.data[y * f.width + x]!;
  }
  return out;
}

/**
 * 반대쪽 뷰가 없는 방위를 거울상으로 채운다.
 *
 * 3뷰 시트에는 오른쓱 측면(90°)만 있다. 왼쓱(270°)을 보는 면은 어느 뷰도 안 보이므로
 * 텍스처 가중치가 전부 0 이 되어 회색 죽이 된다(실측). 캐릭터는 대개 좌우 대칭이니
 * 90° 를 좌우로 뒤집어 270° 로 쓰면 맞는다. 8뷰처럼 이미 채워진 세트는 건드리지 않는다.
 */
export function mirrorFill(set: TurnaroundSet): TurnaroundSet {
  const views = [...set.views];
  const has = (az: number) => views.some((v) => Math.abs(((v.azimuth - az + 540) % 360) - 180) < 30);
  for (const v of set.views) {
    const mirror = (360 - v.azimuth) % 360;
    if (mirror === v.azimuth || has(mirror)) continue;
    // 정면(0)·후면(180)은 자기 자신이 거울이라 건너뛴다. 측면 계열만 채운다.
    const mv = makeView(mirror, mirrorRaster(v.raster), mirrorField(v.mask));
    views.push(mv);
  }
  views.sort((a, b) => a.azimuth - b.azimuth);
  return { ...set, views };
}

/** 뷰의 카메라 방향과 화면 오른쪽 벡터 (xz 평면). */
export function viewBasis(azimuthDeg: number): { dx: number; dz: number; rx: number; rz: number } {
  const t = (azimuthDeg * Math.PI) / 180;
  return { dx: Math.sin(t), dz: Math.cos(t), rx: Math.cos(t), rz: -Math.sin(t) };
}

/**
 * 뷰의 정규화 좌표 샘플러. s: 0..1 가로(bbox 폭), t: 0..1 세로(0=바닥).
 * 헐과 텍스처 투영이 둘 다 이걸 쓴다.
 */
export function viewSampler(view: ViewImage) {
  const { mask, bbox } = view;
  const bw = Math.max(1, bbox.x1 - bbox.x0);
  const bh = Math.max(1, bbox.y1 - bbox.y0);
  return {
    sample(s: number, t: number): number {
      if (s < 0 || s > 1 || t < 0 || t > 1) return 0;
      const xi = Math.round(bbox.x0 + s * bw);
      const yi = Math.round(bbox.y1 - t * bh);
      if (xi < 0 || yi < 0 || xi >= mask.width || yi >= mask.height) return 0;
      return mask.data[yi * mask.width + xi]!;
    },
    /** (s,t) → 뷰 이미지 픽셀 좌표 */
    toPixel(s: number, t: number): [number, number] {
      return [bbox.x0 + s * bw, bbox.y1 - t * bh];
    },
  };
}
